import { useCallback } from "react";
import { Button, Flex, TextField } from "@radix-ui/themes";
import Select, { SelectOption } from "@/components/shared/select";
import { RocketIcon } from "@radix-ui/react-icons";
import {
  CurlRequest,
  httpMethods,
  isHttpMethod,
} from "@/v2/entities/curl-request";
import { useCurlRequestTransformer } from "@/v2/entities/curl-request";

const httpMethodOptions: SelectOption[] = httpMethods.map((method) => ({
  label: method,
  value: method,
}));

interface SkillUrlProps {
  value: CurlRequest;
  loadingSimulators: boolean;
  onChange: (value: CurlRequest) => void;
  onImportFromCurl: () => void;
  onTest: () => void;
}

export function SkillUrl({
  value,
  onChange,
  onImportFromCurl,
  onTest,
  loadingSimulators,
}: SkillUrlProps) {
  const { parsePathParams } = useCurlRequestTransformer();

  const handleMethodChange = useCallback(
    (newMethod: string) => {
      if (isHttpMethod(newMethod)) {
        onChange({ ...value, method: newMethod });
      }
    },
    [value, onChange]
  );

  const handleUrlChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newUrl = e.target.value;
      const urlParts = newUrl.split("?");
      const queryString = new URLSearchParams(urlParts[1]);

      const queryParams: Record<string, string | string[]> = {};
      queryString.forEach((value, key) => {
        queryParams[key] = value;
      });

      // Parse path parameters from the URL
      const { pathParams, cleanUrl } = parsePathParams(urlParts[0]);

      // Merge with existing path parameters, preserving values that were already set
      const mergedPathParams = { ...value.pathParams };
      Object.keys(pathParams).forEach(key => {
        // If this is a new path parameter (not in existing params), initialize it
        if (!(key in mergedPathParams)) {
          mergedPathParams[key] = pathParams[key];
        }
      });

      // Remove path parameters that are no longer in the URL
      const urlPathParamKeys = new Set(Object.keys(pathParams));
      Object.keys(mergedPathParams).forEach(key => {
        if (!urlPathParamKeys.has(key)) {
          delete mergedPathParams[key];
        }
      });

      onChange({
        ...value,
        url: newUrl, // Keep the original URL with placeholders
        queryParams,
        pathParams: Object.keys(mergedPathParams).length > 0 ? mergedPathParams : undefined,
      });
    },
    [value, onChange, parsePathParams]
  );

  return (
    <Flex className="p-1 gap-1 bg-[var(--gray-a2)] rounded-md border border-gray-4">
      <Select
        value={value.method}
        onChange={handleMethodChange}
        options={httpMethodOptions}
        classes={{ root: "min-w-[100px]" }}
        size="medium"
      />

      <TextField.Root
        value={value.url}
        onChange={handleUrlChange}
        color="gray"
        variant="soft"
        className="flex-1 bg-transparent"
        placeholder="https://example.com"
      />

      <Button variant="outline" color="gray" onClick={onImportFromCurl}>
        Import cURL
      </Button>

      <Button
        variant="solid"
        color="gray"
        highContrast
        onClick={onTest}
        disabled={loadingSimulators}
      >
        <RocketIcon />
        Test
      </Button>
    </Flex>
  );
}
